import React from 'react';
import {
  AlertTriangle,
  ShieldAlert,
  HeartPulse,
  Search,
  AlertCircle,
  ShieldCheck,
  Timer,
  ChevronRight
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

/**
 * Subcomponente para secciones críticas (Qué hacer, Cómo prevenir, etc.)
 */
const CriticalSection = ({
  title,
  icon: Icon,
  items,
  bgColor = 'bg-orange-50/50',
  textColor = 'text-orange-500'
}) => (
  <div className={cn('rounded-lg p-4 space-y-3', bgColor)}>
    {/* Título de la sección */}
    <div className="flex items-center gap-2">
      <Icon className={cn('h-5 w-5', textColor)} />
      <h4 className="text-lg font-medium text-gray-900">{title}</h4>
    </div>

    {/* Listado de items */}
    <div className="space-y-2">
      {items.map((item, idx) => (
        <div
          key={idx}
          className="flex items-start gap-2 bg-white/60 p-3 rounded-lg"
        >
          <ChevronRight className={cn('h-4 w-4 mt-1 flex-shrink-0', textColor)} />
          <p className="text-base text-gray-700">{item}</p>
        </div>
      ))}
    </div>
  </div>
);

/**
 * Componente principal: CondicionesCriticasCard
 */
const CondicionesCriticasCard = ({ condicionesCriticas }) => {
  if (!condicionesCriticas?.length) return null;

  return (
    <Card className="overflow-hidden bg-white">
      {/* Encabezado */}
      <CardHeader className="bg-gradient-to-r from-red-500/10 to-transparent border-b border-red-100/50 p-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-red-500 to-red-600 rounded-lg shadow-sm">
            <AlertTriangle className="h-5 w-5 text-white" />
          </div>
          <div>
            <CardTitle className="text-xl font-semibold text-gray-800">
              Condiciones Críticas
            </CardTitle>
            <p className="text-base text-gray-500 mt-1">
              Información importante para emergencias
            </p>
          </div>
        </div>
      </CardHeader>

      {/* Contenido */}
      <CardContent className="p-4 space-y-4">
        {condicionesCriticas.map((condicion, idx) => (
          <div key={idx} className="space-y-4">
            {/* Encabezado de la condición */}
            <div className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden">
              <div className="p-4 space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <ShieldAlert className="h-5 w-5 text-red-500" />
                      <h3 className="text-lg font-medium text-gray-900">
                        {condicion.nombre}
                      </h3>
                    </div>
                    <p className="text-base text-gray-700">
                      {condicion.descripcion}
                    </p>
                  </div>
                  {condicion.nivelRiesgo && (
                    <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-red-100 text-red-700">
                      Riesgo {condicion.nivelRiesgo}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Acciones inmediatas */}
            <CriticalSection
              title="¡Qué hacer inmediatamente!"
              icon={HeartPulse}
              items={condicion.queHacer}
              bgColor="bg-red-50/50"
              textColor="text-red-500"
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Cómo prevenirlo */}
              <CriticalSection
                title="Cómo prevenirlo"
                icon={ShieldCheck}
                items={condicion.comoPrevenirlo}
                bgColor="bg-emerald-50/50"
                textColor="text-emerald-500"
              />

              {/* Señales de alerta */}
              <CriticalSection
                title="Señales de alerta"
                icon={Search}
                items={condicion.comoIdentificarlo}
                bgColor="bg-amber-50/50"
                textColor="text-amber-500"
              />
            </div>

            {/* Recordatorio de velocidad de acción */}
            <div className="bg-red-50 rounded-lg border border-red-100 p-4">
              <div className="flex items-center gap-2">
                <Timer className="h-5 w-5 text-red-500" />
                <p className="text-base font-medium text-red-700">
                  ¡Recuerda! La rapidez de acción es crucial ante estas señales.
                </p>
              </div>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default CondicionesCriticasCard;
